import React from 'react'
import { useTranslation } from 'i18n'
import { Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import errorIcon from 'images/error-icon.svg'
import { phoneRegExp } from 'components/forms/validations'
import { MenuItem } from '@mui/material'

const getCascadingFieldIds = cascadingDropdowns => {
  if (!cascadingDropdowns || !Array.isArray(cascadingDropdowns)) return []
  const ids = []
  cascadingDropdowns.forEach(cdd => {
    if (cdd.primaryField?.id) ids.push(cdd.primaryField.id)
    cdd.dependentFields?.forEach(dep => {
      if (dep.id) ids.push(dep.id)
    })
  })
  return ids
}

// Id of the option currently selected in a cascading dropdown field (primary or dependent).
const getSelectedOptionId = (cascadingDropdowns, values, fieldId) => {
  if (!Array.isArray(cascadingDropdowns) || !fieldId) return null
  const selectedValue = values?.[fieldId]
  if (!selectedValue) return null
  for (const cdd of cascadingDropdowns) {
    if (cdd.primaryField?.id === fieldId) {
      return cdd.primaryField.options?.find(o => o.value === selectedValue)?.id ?? null
    }
    const dep = cdd.dependentFields?.find(d => d.id === fieldId)
    if (dep) {
      const options = dep.optionsMap?.[values[dep.dependsOn]] || []
      return options.find(o => o.value === selectedValue)?.id ?? null
    }
  }
  return null
}

// BR-5708: a custom field with `showWhen` only appears when one of its options is picked in
// the given dropdown (e.g. "Please specify" under "Other"). Hidden fields are neither
// required nor submitted.
const isCustomFieldShown = (field, cascadingDropdowns, values) => {
  const optionIds = field?.showWhen?.optionIds
  if (!field?.showWhen?.fieldId || !Array.isArray(optionIds) || optionIds.length === 0) {
    return true
  }
  const selectedOptionId = getSelectedOptionId(
    cascadingDropdowns,
    values,
    field.showWhen.fieldId,
  )
  return selectedOptionId != null && optionIds.includes(selectedOptionId)
}

const buildCustomTextsFromCascading = (cascadingDropdowns, values) => {
  const customTexts = {}
  if (!cascadingDropdowns || !Array.isArray(cascadingDropdowns))
    return customTexts
  let customTextIndex = 1
  cascadingDropdowns.forEach(cdd => {
    const primaryValue = values[cdd.primaryField?.id]
    customTexts[`customText${customTextIndex}`] = primaryValue ?? ''
    customTextIndex += 1

    cdd.dependentFields?.forEach(dep => {
      const depValue = values[dep.id]
      customTexts[`customText${customTextIndex}`] = depValue ?? ''
      customTextIndex += 1
    })
  })
  return customTexts
}

const buildFormStepEntry = (
  cascadingDropdowns,
  values,
  additionalSlots,
  slotsEnabled,
  customFields,
) => {
  const hasDropdowns =
    Array.isArray(cascadingDropdowns) && cascadingDropdowns.length > 0
  const hasCustomFields =
    Array.isArray(customFields) && customFields.length > 0

  if (!hasDropdowns && !slotsEnabled && !hasCustomFields) return null

  const selections = []
  if (hasDropdowns) {
    cascadingDropdowns.forEach(cdd => {
      const primaryField = cdd.primaryField
      const selectedValue = values[primaryField?.id]
      if (selectedValue) {
        const option = primaryField.options?.find(o => o.value === selectedValue)
        if (option?.id) {
          selections.push({
            fieldKey: primaryField.fieldKey,
            optionId: option.id,
            optionValue: option.label ?? option.value,
          })
        }
      }

      cdd.dependentFields?.forEach(dep => {
        const depValue = values[dep.id]
        if (depValue) {
          const parentValue = values[dep.dependsOn]
          const options = dep.optionsMap?.[parentValue] || []
          const option = options.find(o => o.value === depValue)
          if (option?.id) {
            selections.push({
              fieldKey: dep.fieldKey,
              optionId: option.id,
              optionValue: option.label ?? option.value,
            })
          }
        }
      })
    })
  }

  const customFieldValues = hasCustomFields
    ? customFields
        .filter(field => isCustomFieldShown(field, cascadingDropdowns, values))
        .map(field => ({
          name: field.name,
          label: field.label,
          value: values[field.name] ?? '',
        }))
    : []

  return {
    selections,
    additionalSlots: additionalSlots ?? [],
    customFieldValues,
  }
}

const POSTFormPayloadContructor = (stepFormData, values) => {
  // Our API needs the collection of confirmations sent to it in a collection (not as fields)
  const confirmations = {}
  if (stepFormData.checkboxes && Array.isArray(stepFormData.checkboxes)) {
    stepFormData.checkboxes.forEach(confirmationField => {
      if (values.hasOwnProperty(confirmationField.id)) {
        confirmations[confirmationField.id] = values[confirmationField.id]
      } else {
        confirmations[confirmationField.id] = false
      }
    })
  }

  const cascadingIds = getCascadingFieldIds(stepFormData.cascadingDropdowns)
  const valuesWithoutCascading = { ...values }
  cascadingIds.forEach(id => {
    delete valuesWithoutCascading[id]
  })

  const customTexts = buildCustomTextsFromCascading(
    stepFormData.cascadingDropdowns,
    values,
  )

  return { ...valuesWithoutCascading, confirmations, ...customTexts }
}

const buildFormStepValues = (stepFormData, formikValues) => {
  const values = {}

  if (stepFormData.fields && Array.isArray(stepFormData.fields)) {
    stepFormData.fields.forEach(field => {
      const value = formikValues[field.type]
      if (value !== undefined && value !== null && value !== '') {
        values[field.type] = value
      }
    })
  }

  if (stepFormData.checkboxes && Array.isArray(stepFormData.checkboxes)) {
    stepFormData.checkboxes.forEach(checkbox => {
      values[`checkbox_${checkbox.id}`] = Boolean(formikValues[checkbox.id])
    })
  }

  // Admin-defined custom fields (BR-5337) are keyed by their stable name and
  // submitted alongside the regular fields; the API validates required ones
  // against the form schema.
  if (stepFormData.customFields && Array.isArray(stepFormData.customFields)) {
    stepFormData.customFields.forEach(field => {
      if (!isCustomFieldShown(field, stepFormData.cascadingDropdowns, formikValues)) return
      const value = formikValues[field.name]
      if (value !== undefined && value !== null && value !== '') {
        values[field.name] = value
      }
    })
  }

  // Cascading/inventory selections now travel in formStepEntry.selections
  // (see buildFormStepEntry). The top-level inventorySelections is the legacy
  // lead-gen field that formStepEntry replaces, so it stays empty here.
  const inventorySelections = []

  return { values, inventorySelections }
}

const renderTextInput = props => {
  const errorExists = props.error || props.errors

  return (
    <FormControl
      className={`field field-${props.field.name}${props.index || ''}`}
      key={props.field.name}
    >
      <TextField
        error={props.error && props.touched && true}
        // helperText={props.error && props.touched && props.error} // this appears below the field
        label={props.label}
        type={props.name}
        value={props.field.value}
        variant="filled"
        name={props.field.name}
        id={`${props.field.name}-helper`}
        onChange={e => {
          props.form.setFieldValue(props.field.name, e.target.value)
        }}
      />
      {errorExists && props.touched && (
        <div className="error">
          <ErrorMessage name={props.field.name} />
        </div>
      )}
      {errorExists && props.touched && (
        <img className="error-icon" src={errorIcon} alt="" />
      )}
    </FormControl>
  )
}

// leadgen fields are a fixed set of field "types"
const MAPPINGS = {
  firstName: {
    labelKey: 'fields.firstName',
    type: 'text',
    placeholder: '',
    required: true,
  },
  lastName: {
    labelKey: 'fields.lastName',
    type: 'text',
    placeholder: '',
    required: true,
  },
  fullName: {
    labelKey: 'fields.fullName',
    type: 'text',
    placeholder: '',
  },
  email: {
    labelKey: 'fields.email',
    type: 'email',
    placeholder: '',
    required: true,
  },
  phoneNumber: {
    labelKey: 'fields.phoneNumber',
    type: 'text',
    placeholder: '',
  },
}

// render a Formik component for our leadgen field types
// `t` comes from the calling component's useTranslation('form') — this is a
// plain helper, not a component, so it cannot call the hook itself.
const getFormFieldByType = ({ field, formik, index, t }) => {
  let required = field.required || false
  let requiredString = required ? '*' : '' //add an asterisk, but only if req'd

  if (MAPPINGS[field.type] && MAPPINGS[field.type].type === 'text') {
    let placeholder =
      field.label || field.displayTitle || MAPPINGS[field.type].placeholder
    let label =
      field.label || field.displayTitle || t(MAPPINGS[field.type].labelKey)
    return (
      <Field
        key={index}
        id={field.type}
        name={field.type}
        type="text"
        label={`${label}${requiredString}`}
        placeholder={placeholder}
        component={renderTextInput}
        error={formik.errors[field.type]}
        touched={formik.touched[field.type] && true}
      />
    )
  }
  if (field.type === 'email') {
    return (
      <Field
        key={index}
        id={field.type}
        name={field.type}
        type="email"
        component={renderTextInput}
        label={`${
          field.label || t(MAPPINGS[field.type].labelKey)
        }${requiredString}`}
        placeholder={field.label || t('fields.email')}
        value={formik.values.email}
        errors={formik.errors.email}
        touched={formik.touched.email && true}
      />
    )
  }

  return <p>{t('unhandledFieldType', { type: field.type })}</p>
}

const FormField = ({ field, formik, index }) => {
  const { t } = useTranslation('form')
  return getFormFieldByType({ field, formik, index, t })
}

const renderCascadingSelectInput = ({
  id,
  label,
  options,
  value,
  onChange,
  error,
  touched,
  inventoryConfig,
  t,
}) => {
  const errorExists = error

  return (
    <FormControl variant="filled" className={`field field-select`} key={id}>
      <InputLabel htmlFor={`${id}-select`}>{label}</InputLabel>
      <Select
        value={value}
        variant="filled"
        onChange={e => onChange(e.target.value)}
        inputProps={{
          name: id,
          id: `${id}-select`,
        }}
        MenuProps={{
          disableScrollLock: true,
          autoFocus: false,
          disableAutoFocusItem: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
        }}
      >
        {options.map((option, i) => {
          const isSoldOut =
            option.inventorySize != null &&
            option.occupiedCount >= option.inventorySize

          let remainingCopy = null
          if (
            !isSoldOut &&
            inventoryConfig?.showRemainingInventory &&
            inventoryConfig?.remainingInventoryThreshold != null &&
            option.inventorySize != null
          ) {
            const occupiedCount = option.occupiedCount ?? 0
            const remaining = option.inventorySize - occupiedCount
            if (remaining <= inventoryConfig.remainingInventoryThreshold) {
              remainingCopy = inventoryConfig.remainingInventoryCopy?.replace(
                /#/g,
                remaining,
              ) ?? null
            }
          }

          return (
            <MenuItem
              key={option.id || i}
              value={option.value}
              disabled={isSoldOut}
              sx={isSoldOut ? { color: '#767676' } : undefined}
            >
              {option.label}
              {isSoldOut && ` ${t('optionUnavailable')}`}
              {remainingCopy && (
                <span className="remaining-inventory-copy">{remainingCopy}</span>
              )}
            </MenuItem>
          )
        })}
      </Select>

      {errorExists && touched && (
        <>
          <div className="error">{errorExists}</div>
          <img className="error-icon" src={errorIcon} alt="" />
        </>
      )}
    </FormControl>
  )
}

const renderCascadingDropdowns = (
  cascadingDropdowns,
  formik,
  inventoryConfig,
  t,
) => {
  if (!cascadingDropdowns) return null

  return cascadingDropdowns.map(cdd => {
    const primaryValue = formik.values[cdd.primaryField.id]

    return (
      <div className="cascading-dropdown-group" key={cdd.id}>
        {renderCascadingSelectInput({
          id: cdd.primaryField.id,
          label: cdd.primaryField.label,
          options: cdd.primaryField.options,
          value: primaryValue,
          error: formik.errors[cdd.primaryField.id],
          touched: formik.touched[cdd.primaryField.id],
          inventoryConfig,
          t,
          onChange: value => {
            formik.setFieldValue(cdd.primaryField.id, value)
            formik.setFieldTouched(cdd.primaryField.id, false, false)
            // clear all dependent fields when primary changes
            cdd.dependentFields.forEach(dep => {
              formik.setFieldValue(dep.id, '')
            })
          },
        })}

        {cdd.dependentFields.map(dep => {
          const dependsOnValue = formik.values[dep.dependsOn]
          if (!dependsOnValue) return null

          const options = dep.optionsMap[dependsOnValue] || []

          return renderCascadingSelectInput({
            id: dep.id,
            label: dep.label,
            options,
            value: formik.values[dep.id],
            onChange: value => {
              formik.setFieldValue(dep.id, value)
              formik.setFieldTouched(dep.id, false, false)
            },
            error: formik.errors[dep.id],
            touched: formik.touched[dep.id],
            inventoryConfig,
            t,
          })
        })}
      </div>
    )
  })
}

const generateCascadingDropdownValidation = (cascadingDropdowns, t) => {
  if (!cascadingDropdowns) return {}
  const schema = {}
  cascadingDropdowns.forEach(cdd => {
    schema[cdd.primaryField.id] = Yup.string().required(
      t('validation.required'),
    )
    cdd.dependentFields.forEach(dep => {
      schema[dep.id] = Yup.string().when(dep.dependsOn, {
        is: val => !!val,
        then: s => s.required(t('validation.required')),
        otherwise: s => s.notRequired(),
      })
    })
  })
  return schema
}

const generateCustomFieldInitialValues = customFields => {
  if (!customFields || !Array.isArray(customFields)) return {}
  return customFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {})
}

const generateFormValidation = (stepFormData, customFields, t) => {
  const optionalNameValidation = Yup.string()
    .max(80, t('validation.maxLength', { max: 80 }))
    .trim()

  // since we have the same set of fields for form email we can use this same config for all steps
  // we could probably generate this based on our MAPPINGS map above
  const validationSchema = {
    firstName: optionalNameValidation,
    lastName: optionalNameValidation,
    fullName: Yup.string()
      .max(160, t('validation.maxLength', { max: 160 }))
      .trim(),
    email: Yup.string().email(t('validation.invalidEmail')),
    phoneNumber: Yup.string().matches(
      phoneRegExp,
      t('validation.invalidPhone'),
    ),
  }

  let yupSchema = {}
  stepFormData.fields &&
    stepFormData.fields.forEach(function (field) {
      if (validationSchema[field.type]) {
        if (field) {
          if (field.required && field.required === true) {
            yupSchema[field.type] = validationSchema[field.type].required(
              t('validation.required'),
            )
          } else {
            yupSchema[field.type] = validationSchema[field.type]
          }
        }
      }
    })
  let confirmationSchema = {}
  // Checkbox validation: required checkboxes must be checked (true)
  stepFormData.checkboxes &&
    stepFormData.checkboxes.forEach(obj => {
      if (obj.required) {
        confirmationSchema[obj.id] = Yup.bool().oneOf(
          [true],
          t('validation.requiredCheckbox'),
        )
      }
    })
  const cascadingSchema = generateCascadingDropdownValidation(
    stepFormData.cascadingDropdowns,
    t,
  )
  let customFieldSchema = {}
  if (customFields && Array.isArray(customFields)) {
    customFields.forEach(field => {
      if (field.required && field.showWhen) {
        // Required only while shown; the sibling dropdown values decide that.
        customFieldSchema[field.name] = Yup.string()
          .trim()
          .test('required-when-shown', t('validation.required'), function (value) {
            if (!isCustomFieldShown(field, stepFormData.cascadingDropdowns, this.parent)) {
              return true
            }
            return Boolean(value && value.trim())
          })
        return
      }
      customFieldSchema[field.name] = field.required
        ? Yup.string().trim().required(t('validation.required'))
        : Yup.string().trim()
    })
  }
  let finalSchema = { ...confirmationSchema, ...yupSchema, ...cascadingSchema, ...customFieldSchema }
  return Yup.object(finalSchema)
}

const generateCascadingDropdownInitialValues = cascadingDropdowns => {
  if (!cascadingDropdowns) return {}
  const values = {}
  cascadingDropdowns.forEach(cdd => {
    values[cdd.primaryField.id] = ''
    cdd.dependentFields.forEach(dep => {
      values[dep.id] = ''
    })
  })
  return values
}

const generateCheckboxInitialValues = checkboxes => {
  if (!checkboxes || !Array.isArray(checkboxes)) return {}
  return checkboxes.reduce((acc, item) => ({ ...acc, [item.id]: false }), {})
}

export {
  renderTextInput,
  getFormFieldByType,
  FormField,
  MAPPINGS,
  generateFormValidation,
  POSTFormPayloadContructor,
  buildFormStepValues,
  buildFormStepEntry,
  renderCascadingDropdowns,
  generateCascadingDropdownInitialValues,
  generateCheckboxInitialValues,
  generateCustomFieldInitialValues,
  isCustomFieldShown,
}
